import React from 'react';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  List,
  ListItemText,
  ListItemButton,
  Collapse,
  Tooltip,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { IPositionSummary } from '../../../../Models/IPositionSummary';
import { NavItem, NavigationPath } from './BlindTestDashboardTypes';

// Images
import milkCoolerImage from "../../../../Assets/PNG/milkCooler.png";
import steamHeaterImage from "../../../../Assets/PNG/steamheater.png";

interface NavigationItemProps {
  item: NavItem;
  level: number;
  parentPath: NavigationPath;
  expandedItems: Record<string, boolean>;
  selectedPaths: {
    industry?: string;
    subIndustry?: string;
    application?: string;
    subApplication?: string;
  };
  onToggleItem: (name: string, fullPath?: string) => void;
  onSubApplicationClick: (industry: string, subIndustry: string, application: string, subApplication: string) => void;
  styles: any;
}

interface NavigationSidebarProps {
  navItems: NavItem[];
  isLoading: boolean;
  expandedItems: Record<string, boolean>;
  selectedPaths: {
    industry?: string;
    subIndustry?: string;
    application?: string;
    subApplication?: string;
  };
  onToggleItem: (name: string) => void;
  onSubApplicationClick: (industry: string, subIndustry: string, application: string, subApplication: string) => void;
  styles: any;
}

interface PositionSummaryCardProps {
  item: IPositionSummary;
  isActive: boolean;
  isDownloading: boolean;
  isModeler: boolean;
  isSme: boolean;
  onCardClick: () => void;
  onDownload: () => void;
  styles: any;
}

interface ContentDisplayProps {
  isLoading: boolean;
  displaySummaries: boolean;
  selectedSubApplication?: string;
  positionSummaries: IPositionSummary[];
  renderCards: () => React.ReactNode;
  styles: any;
}

/**
 * Helper function to get a unique path identifier
 */
const getPathIdentifier = (level: number, path: NavigationPath, itemName: string): string => {
  switch(level) {
    case 0: return itemName;
    case 1: return `${path.industry}/${itemName}`;
    case 2: return `${path.industry}/${path.subIndustry}/${itemName}`;
    case 3: return `${path.industry}/${path.subIndustry}/${path.application}/${itemName}`;
    default: return itemName;
  }
};

/**
 * Navigation Item component
 */
export const NavigationItem: React.FC<NavigationItemProps> = ({ 
  item, 
  level, 
  parentPath, 
  expandedItems, 
  selectedPaths, 
  onToggleItem, 
  onSubApplicationClick,
  styles 
}) => {
  // Parse item name to handle format with dash
  const tokens = item.name.split("-");
  const heading = tokens[0]?.trim() || "";
  const subHeading = tokens.length > 1 ? tokens[1]?.trim() : "";

  // Build path for this item
  const currentPath = { ...parentPath };
  if (level === 0) currentPath.industry = item.name;
  if (level === 1) currentPath.subIndustry = item.name;
  if (level === 2) currentPath.application = item.name;
  
  // Create a compound path identifier that's unique even with duplicate names
  const pathIdentifier = getPathIdentifier(level, parentPath, item.name);
  
  // Determine if the item should be expanded - check both name and full path
  const isExpanded = 
    expandedItems[item.name] || 
    expandedItems[pathIdentifier] ||
    (level === 0 && selectedPaths.industry === item.name) ||
    (level === 1 && selectedPaths.subIndustry === item.name) ||
    (level === 2 && selectedPaths.application === item.name);

  // Check if this exact item is currently selected using full path matching
  const isSelected = level === 3 && 
    selectedPaths.industry === parentPath.industry &&
    selectedPaths.subIndustry === parentPath.subIndustry &&
    selectedPaths.application === parentPath.application &&
    selectedPaths.subApplication === item.name;

  const handleClick = () => {
    if (level === 3 && currentPath.industry && currentPath.subIndustry && currentPath.application) {
      onSubApplicationClick(
        currentPath.industry,
        currentPath.subIndustry,
        currentPath.application,
        item.name
      );
    } else {
      // Pass both name and full path for better identification
      onToggleItem(item.name, pathIdentifier);
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={styles.navItem.button(level, isSelected)}
        aria-expanded={isExpanded}
        aria-selected={isSelected}
      >
        <ListItemText
          primary={
            <Box>
              <Typography
                variant="body2"
                component="span"
                display="block"
                sx={styles.navItem.text.primary(level, isSelected)}
              >
                {heading}
              </Typography>
              {subHeading && (
                <Typography
                  variant="caption"
                  component="span"
                  display="block"
                  sx={styles.navItem.text.secondary}
                >
                  ({subHeading})
                </Typography>
              )}
            </Box>
          }
        />
        {item.children && item.children.length > 0 && (
          <Box sx={styles.navItem.expandIcon}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        )}
      </ListItemButton>

      {item.children && item.children.length > 0 && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child) => (
              <NavigationItem
                key={getPathIdentifier(level + 1, currentPath, child.name)}
                item={child}
                level={level + 1}
                parentPath={currentPath}
                expandedItems={expandedItems}
                selectedPaths={selectedPaths}
                onToggleItem={onToggleItem}
                onSubApplicationClick={onSubApplicationClick}
                styles={styles}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

/**
 * Navigation sidebar component
 */
export const NavigationSidebar: React.FC<NavigationSidebarProps> = ({ 
  navItems,
  isLoading,
  expandedItems,
  selectedPaths,
  onToggleItem,
  onSubApplicationClick,
  styles 
}) => {
  if (isLoading || !navItems.length) {
    return (
      <Paper elevation={3} sx={styles.navigation.sidebarLoading}>
        <CircularProgress size={50} />
        <Typography variant="body2" sx={styles.navigation.loadingText}>
          Loading Units...
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={styles.navigation.sidebar} className="btd-mui-navigation">
      <Box sx={styles.navigation.header}>
        <Typography variant="subtitle1" fontWeight={600}>
          Units
        </Typography>
      </Box>
      <List
        component="nav"
        aria-labelledby="nested-list-subheader"
        sx={styles.navigation.list}
        className="btd-mui-nav-list"
      >
        {navItems.map((item: NavItem) => (
          <NavigationItem
            key={`item-${item.name}`}
            item={item}
            level={0}
            parentPath={{}}
            expandedItems={expandedItems}
            selectedPaths={selectedPaths}
            onToggleItem={onToggleItem}
            onSubApplicationClick={onSubApplicationClick}
            styles={styles}
          />
        ))}
      </List>
    </Paper>
  );
};

/**
 * Position summary card component
 */
export const PositionSummaryCard: React.FC<PositionSummaryCardProps> = ({
  item,
  isActive,
  isDownloading,
  isModeler,
  isSme,
  onCardClick,
  onDownload,
  styles
}) => {
  const canDownload = item.summary["todo"] === 0;
  const isClickable = !(isModeler && !isSme && item.summary["todo"] > 0);

  return (
    <Paper
      elevation={isActive ? 8 : 2}
      className={isActive ? "active-card" : ""}
      sx={styles.card.container(isActive, isClickable, item.summary["todo"])}
    >
      <Box onClick={isClickable ? onCardClick : undefined} sx={styles.card.content}>
        {item.featureAvailable === false && (
          <Chip
            label="Coming soon ⏳"
            variant="outlined"
            size="small"
            sx={styles.card.comingSoonChip}
          />
        )}

        <Box sx={styles.card.header}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={styles.card.title}>
              {item.alfaLavalPosition}
            </Typography>

            <Typography variant="body2" sx={styles.card.processStage}>
              {item.processStage}
            </Typography>

            <Typography variant="caption" sx={styles.card.productLine}>
              {item.productLine}
            </Typography>

            <Typography variant="caption" component="code" sx={styles.card.datasetId}>
              {item.blindTestDataSetId}
            </Typography>
          </Box>

          <Stack sx={styles.card.chipStack}>
            {!isModeler || canDownload ? (
              <>
                <Chip
                  label={`${item.summary["emelie - bts"]} EMELIE - BTS`}
                  size="small"
                  sx={styles.card.chips.emelieBts}
                />
                <Chip
                  label={`${item.summary["emelie - acceptable"]} EMELIE - A`}
                  size="small"
                  variant="outlined"
                  sx={styles.card.chips.emelieAcceptable}
                />
                <Chip
                  label={`${item.summary["sme"]} SME`}
                  size="small"
                  sx={styles.card.chips.sme}
                />
              </>
            ) : (
              <Chip
                label="in progress"
                size="small"
                variant="outlined"
                sx={styles.card.chips.inProgress}
              />
            )}
          </Stack>
        </Box>

        <Box sx={styles.card.imageContainer}>
          <Box
            component="img"
            src={
              item.alfaLavalPosition.includes("Milk")
                ? milkCoolerImage
                : steamHeaterImage
            }
            alt={item.alfaLavalPosition}
            sx={styles.card.image}
          />
        </Box>
      </Box>

      <Stack sx={styles.card.footer}>
        <Stack sx={styles.card.footerActions}>
          <Box sx={styles.card.footerBox}>
            <Box>
            <Chip
              label={`${item.summary["todo"]} To do`}
              size="small"
              color={!canDownload ? "primary" : "default"}
              variant={!canDownload ? "filled" : "outlined"}
              sx={{mr: 1}}
            />
            <Chip
              label={`${
                item.summary["emelie - bts"] +
                item.summary["emelie - acceptable"] +
                item.summary["sme"]
              } Completed`}
              size="small"
              color={canDownload ? "success" : "default"}
              variant={canDownload ? "filled" : "outlined"}
            /></Box>
             <Tooltip
            title={
              canDownload
                ? "Download design"
                : "All tests must be completed by SME in order to download the sample."
            }
          >
            <Box component="span">
              <IconButton
                disabled={!canDownload || isDownloading}
                onClick={onDownload}
                size="small"
                color="primary"
                aria-label="Download design"
              >
                {isDownloading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadIcon />
                )}
              </IconButton>
            </Box>
          </Tooltip>
          </Box>

         
        </Stack>
      </Stack>
    </Paper>
  );
};

/**
 * Content Display Component
 */
export const ContentDisplay: React.FC<ContentDisplayProps> = ({
  isLoading,
  displaySummaries,
  selectedSubApplication,
  positionSummaries,
  renderCards,
  styles,
}) => {
  if (!displaySummaries) {
    return null;
  }

  return (
    <Stack sx={styles.contentArea}>
      {selectedSubApplication && (
        <Box sx={styles.content.header}>
          <Typography variant="h5" sx={styles.content.title}>
            {selectedSubApplication}
          </Typography>
        </Box>
      )}
      
      <Divider sx={styles.content.divider} />
      
      <Box>
        {isLoading ? (
          <Box sx={styles.content.loading}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={styles.content.loadingText}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.content.cardsContainer}>
            {renderCards()}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

/**
 * Empty Results Display Component
 */
export const EmptyResultsDisplay: React.FC<{ styles: any }> = ({ styles }) => (
  <Box className="btd-mui-empty-results" sx={styles.content.emptyResults}>
    <Typography variant="body1">
      No results to display under this process.
    </Typography>
  </Box>
);

/**
 * Performance Metrics Display Component
 */
export const PerformanceMetricsDisplay: React.FC<{ 
  performanceMetrics: Record<string, number>;
}> = ({ performanceMetrics }) => {
  const hasMetrics = Object.keys(performanceMetrics).length > 0;
  
  if (!hasMetrics) {
    return null;
  }
  
  return (
    <Box sx={{ 
      position: 'fixed', 
      top: 10, 
      right: 10, 
      bgcolor: 'background.paper', 
      p: 1, 
      borderRadius: 1,
      zIndex: 9999,
      fontSize: '0.75rem',
      opacity: 0.8,
      maxWidth: 300
    }}>
      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
        Performance Metrics (ms):
      </Typography>
      {Object.entries(performanceMetrics).map(([key, value]) => (
        <Typography key={key} variant="caption" sx={{ display: 'block' }}>
          {key}: {value.toFixed(2)}ms
        </Typography>
      ))}
    </Box>
  );
}; 